import classNames from 'classnames';

import { ArtifactType } from '@/shared/types';

import { createArtifactRequestHeaders } from '../../helpers/createArtifactRequestHeaders';
import { clientPublic, client } from '../core/client';
import {
  FeedbackDTO,
  FeedbackType,
  ResponseAccordionText,
  FAQ,
  LabelValue,
  ResponseFeatures,
  ResponseNotifications,
  ResponseUserData,
  ResponseUserWeapons,
  ResponseUserWeapons_v2,
  UserRequest,
  DataResponse,
  ResponseArtifact,
  OrderPayload,
} from '../types';
import { PaginatedResponse } from '../types/common';
import { UserRequestFiltersOptionsV2, UserRequestScopeV2 } from '../types/requests';

export const common = {
  getFeatures: (force = false): Promise<ResponseFeatures> => {
    return client.get('/features' + (force ? '?force=true' : ''));
  },
  getUserRequestsV2: (params: {
    scope: UserRequestScopeV2;
    page?: number;
    per_page?: number;
    filters?: Partial<{
      channel: UserRequest['channel'];
      service_code: UserRequest['service_code'];
      service_status_name: string;
      created_at_from: string;
      created_at_to: string;
    }>;
  }): Promise<PaginatedResponse<UserRequest[]>> => {
    const query: Record<string, string> = {
      scope: params.scope,
      'filters[scope]': params.scope,
      per_page: String(params.per_page ?? 15),
      page: String(params.page ?? 1),
    };

    Object.entries(params.filters ?? {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      query[`filters[${key}]`] = String(value);
    });

    return client.get(`/user-requests?${new URLSearchParams(query).toString()}`, undefined, 'v2');
  },
  getUserRequestFiltersV2: (scope?: UserRequestScopeV2): Promise<DataResponse<UserRequestFiltersOptionsV2>> =>
    client.get(`/user-requests/filters${scope ? `?scope=${scope}` : ''}`, undefined, 'v2'),
  downloadUserRequestArtifact: (
    requestId: UserRequest['id'],
    artifactType: ArtifactType,
    artifactKey = '',
  ): Promise<ResponseArtifact> => {
    return client.get(
      `/user-requests/${requestId}/artifacts/${artifactType}${classNames({ [`?key=${artifactKey}`]: artifactKey })}`,
      {
        headers: createArtifactRequestHeaders(artifactType),
      },
    );
  },
  downloadUserRequestPartArtifact: (partId: string, artifactType: ArtifactType): Promise<ResponseArtifact> => {
    return client.get(`/user-requests/parts/${partId}/artifacts/${artifactType}`, {
      headers: createArtifactRequestHeaders(artifactType),
    });
  },
  getUserData: (): Promise<ResponseUserData> => {
    return client.get('/users');
  },
  getNotifications: (page: number): Promise<ResponseNotifications> => {
    return client.get(`/notifications?page=${page}`);
  },
  updateUserProfile: (data: Partial<OrderPayload>): Promise<ResponseUserData> => {
    return client.patch('/users', data);
  },
  getWeaponsData: (): Promise<DataResponse<ResponseUserWeapons>> => {
    return client.get(`/weapons`);
  },
  getWeaponsData_v2: (): Promise<DataResponse<ResponseUserWeapons_v2>> => {
    return client.get(`/weapons`, {}, 'v2');
  },
  getAccordionsText: (): Promise<DataResponse<ResponseAccordionText>> => {
    return clientPublic.get('/accordionsText');
  },
  proxyQRSearchRequest: (qrUrl: string): Promise<DataResponse<any>> => {
    return clientPublic.get(`/criminal-record-search${qrUrl}`);
  },
  getStatistics: (): Promise<DataResponse<LabelValue[]>> => {
    return clientPublic.get('/statistics');
  },
  mapsAuth: (): Promise<{
    token: string;
    expires: number;
    ssl: boolean;
  }> => {
    return client.post('/maps/auth');
  },
  getFaq: (): Promise<DataResponse<FAQ[]>> => {
    return clientPublic.get('/faq');
  },
  sendFeedback: (type: FeedbackType, payload: FeedbackDTO): Promise<void> => {
    return client.post(`/feedback/${type}`, payload);
  },
};
